import { CommonModule } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { LocalStorageKeys } from 'src/app/@core/models/local-storage-keys'
import { RedirectModalServiceService } from './redirect-modal-service.service'
import { RedirectModalRepository } from './state/redirect-modal.repository'
import { PlatformDetectorService } from 'src/app/@core/services/platform-detector/platform-detector.service'

@Component({
  selector: 'app-redirect-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './redirect-modal.component.html',
  styleUrl: './redirect-modal.component.scss'
})
export class RedirectModalComponent {
  dontShowAgain: boolean = false
  clickOutside = true

  private readonly APP_STORE_URL =
    'https://apps.apple.com/br/app/id6744837737'

  private readonly PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.esporteshub'
  private readonly APP_OPEN_TIMEOUT = 2000

  constructor(
    private service: RedirectModalServiceService,
    private repository: RedirectModalRepository,
    private platformDetector: PlatformDetectorService
  ) {}

  handleClickOutside() {
    if (this.clickOutside) {
      this.closeModal()
    }

    this.clickOutside = true
  }

  handleClickInside() {
    this.clickOutside = false
  }

  /**
   * Tenta abrir o app e retorna uma Promise que resolve quando o processo termina
   */
  openApp(): Promise<'opened' | 'failed'> {
    return new Promise(resolve => {
      const appUrl = `esporteshub://redirect?path=${this.repository.path}`
      const isIOS = this.platformDetector.isIOS()

      if (isIOS) {
        this.openAppIOS(appUrl).then(resolve)
      } else {
        this.openAppAndroid(appUrl).then(resolve)
      }
    })
  }

  /**
   * Técnica específica para iOS
   */
  private openAppIOS(appUrl: string): Promise<'opened' | 'failed'> {
    return new Promise(resolve => {
      // Registra o tempo inicial
      const startTime = Date.now()

      // Registra se houve blur (possível indicação de que o app abriu)
      let blurred = false
      const blurHandler = () => {
        blurred = true
      }
      window.addEventListener('blur', blurHandler)

      // Tenta todas as técnicas possíveis em sequência

      // 1. Técnica do iframe (pode funcionar em algumas versões do iOS)
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = appUrl
      document.body.appendChild(iframe)

      // 2. Técnica de location.href após um pequeno delay
      setTimeout(() => {
        document.body.removeChild(iframe)

        // Se ainda não abriu, tenta diretamente
        if (!blurred) {
          window.location.href = appUrl
        }

        // Verifica após um tempo se o app foi aberto
        setTimeout(() => {
          // Remove o event listener
          window.removeEventListener('blur', blurHandler)

          // Se a página não perdeu foco e passou tempo suficiente,
          // provavelmente o app não está instalado
          if (
            !blurred &&
            Date.now() - startTime < this.APP_OPEN_TIMEOUT * 1.5
          ) {
            resolve('failed')
          } else {
            resolve('opened')
          }
        }, this.APP_OPEN_TIMEOUT)
      }, 100)
    })
  }

  /**
   * Técnica para Android
   */
  private openAppAndroid(appUrl: string): Promise<'opened' | 'failed'> {
    return new Promise(resolve => {
      // Registra o tempo inicial
      const startTime = Date.now()

      // Registra se houve blur (possível indicação de que o app abriu)
      let blurred = false
      const blurHandler = () => {
        blurred = true
      }
      window.addEventListener('blur', blurHandler)

      // Tenta abrir o app
      window.location.href = appUrl

      // Verifica após um tempo
      setTimeout(() => {
        // Remove o event listener
        window.removeEventListener('blur', blurHandler)

        if (!blurred && Date.now() - startTime < this.APP_OPEN_TIMEOUT * 1.5) {
          resolve('failed')
        } else {
          resolve('opened')
        }
      }, this.APP_OPEN_TIMEOUT)
    })
  }

  /**
   * Abre a loja de aplicativos apropriada
   */
  openStore(): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    window.location.href = isIOS ? this.APP_STORE_URL : this.PLAY_STORE_URL
  }

  continueBrowser(): void {
    this.closeModal()
  }

  closeModal() {
    if (this.dontShowAgain) {
      localStorage.setItem(LocalStorageKeys.redirectModal, 'true')
    }
    this.service.closeModal()
  }
}
